import React from "react";
import { format } from "date-fns";
import type { CalendarEvent } from "../../types/types";
import { calculateEventMetrics } from "../../utils/eventCalculations";

interface ChartCardProps {
  events: CalendarEvent[];
  revenueData: any[]; // <-- new prop
  currency?: string;
  granularity?: string;
  windowDays?: number;
  onEventSelect?: (event: CalendarEvent | null) => void;
  selectedEvent?: CalendarEvent | null;
  isLoading?: boolean;
  error?: any;
}

const ChartCard: React.FC<ChartCardProps> = ({
  events,
  revenueData,
  currency = "$",
  windowDays = 3,
  onEventSelect,
  selectedEvent,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return (
      <div className="w-80 h-96 bg-muted/20 rounded-lg border border-border/50 p-4 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          <span className="text-sm text-muted-foreground">Loading events...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-80 h-96 bg-muted/20 rounded-lg border border-border/50 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">⚠️</div>
          <div className="text-sm text-muted-foreground">Error loading revenue data</div>
        </div>
      </div>
    );
  }

  // Calculate metrics for each event
  const eventMetrics = events.map((evt) => ({
    ...evt,
    ...calculateEventMetrics(evt, revenueData ?? [], windowDays),
  }));

  return (
    <div className="w-80 h-96 bg-gradient-to-br from-background to-muted/30 rounded-xl border border-border/60 shadow-sm backdrop-blur-sm p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-3 w-3 rounded-full bg-gradient-to-r from-accent to-accent/80 shadow-sm"></div>
            <div className="absolute inset-0 h-3 w-3 rounded-full bg-accent/20 animate-pulse"></div>
          </div>
          <div>
            <h3 className="font-semibold text-card-foreground text-base">
              Events
            </h3>
            <div className="text-xs text-muted-foreground font-medium">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="space-y-3 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
        {eventMetrics.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="text-4xl mb-3 opacity-40">📅</div>
            <div className="text-sm font-medium text-muted-foreground mb-1">No events found</div>
            <div className="text-xs text-muted-foreground/70">Events will appear here when available</div>
          </div>
        ) : (
          eventMetrics.map((evt, index) => (
            <div
              key={evt.id}
              className={`group relative p-4 rounded-xl border transition-all duration-200 ${
                selectedEvent?.id === evt.id
                  ? "bg-gradient-to-r from-accent/10 to-accent/5 border-accent/60 shadow-md ring-1 ring-accent/20"
                  : "bg-background/80 border-border/40 hover:bg-muted/40 hover:border-border/60 hover:shadow-sm"
              }`}
              onClick={() => onEventSelect?.(selectedEvent?.id === evt.id ? null : evt)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Event Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-card-foreground text-sm mb-1 line-clamp-1">
                    {evt.title}
                  </div>
                  {evt.description && (
                    <div className="text-xs text-muted-foreground/80 mb-2 line-clamp-2">
                      {evt.description}
                    </div>
                  )}
                  <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-muted/50 text-xs text-muted-foreground font-medium">
                    <span className="mr-1">📅</span>
                    {format(new Date(evt.startAt), "MMM d")} → {format(new Date(evt.endAt), "MMM d")}
                  </div>
                </div>
                <div className={`ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                  selectedEvent?.id === evt.id ? 'opacity-100' : ''
                }`}>
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                </div>
              </div>

              {/* Revenue Metrics */}
              <div className="space-y-2.5 pt-3 border-t border-border/30">
                <div className="grid grid-cols-1 gap-2">
                  {/* Before Revenue */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Before ({windowDays} {windowDays === 1 ? "day" : "days"})
                    </span>
                    <span className="text-xs font-semibold text-card-foreground">
                      {currency}
                      {evt.beforeRevenue !== null
                        ? evt.beforeRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "N/A"}
                    </span>
                  </div>

                  {/* During Revenue */}
                  <div className="flex items-center justify-between bg-muted/30 rounded-md px-2 py-1.5">
                    <span className="text-xs font-medium text-card-foreground">
                      During event
                    </span>
                    <span className="text-sm font-bold text-card-foreground">
                      {currency}
                      {evt.duringRevenue !== null
                        ? evt.duringRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "N/A"}
                    </span>
                  </div>

                  {/* After Revenue */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      After ({windowDays} {windowDays === 1 ? "day" : "days"})
                    </span>
                    <span className="text-xs font-semibold text-card-foreground">
                      {currency}
                      {evt.afterRevenue !== null
                        ? evt.afterRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {/* Growth Metrics */}
                {(evt.growthBeforeToDuring !== null || evt.growthDuringToAfter !== null) && (
                  <div className="pt-2 border-t border-border/20">
                    <div className="grid grid-cols-1 gap-2">
                      {evt.growthBeforeToDuring !== null && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Before → During</span>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs font-semibold ${
                              evt.growthBeforeToDuring > 0 ? "text-emerald-600" : "text-red-500"
                            }`}>
                              {evt.growthBeforeToDuring > 0 ? "+" : ""}{evt.growthBeforeToDuring.toFixed(1)}%
                            </span>
                            <span className="text-xs">
                              {evt.growthBeforeToDuring > 0 ? "📈" : "📉"}
                            </span>
                          </div>
                        </div>
                      )}
                      {evt.growthDuringToAfter !== null && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">During → After</span>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs font-semibold ${
                              evt.growthDuringToAfter > 0 ? "text-emerald-600" : "text-red-500"
                            }`}>
                              {evt.growthDuringToAfter > 0 ? "+" : ""}{evt.growthDuringToAfter.toFixed(1)}%
                            </span>
                            <span className="text-xs">
                              {evt.growthDuringToAfter > 0 ? "📈" : "📉"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChartCard;